import React from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle } from "./PersonaUi";
import { ER, INDEX_FEE, OBS_F, RF, SHORT, aer, effN, isIndexLike, usd } from "./riaFundsData";

const heat = (v) => (v >= 50 ? "bg-red-500/60" : v >= 35 ? "bg-amber-500/50" : v >= 20 ? "bg-amber-500/20" : "bg-zinc-800");

export default function RiaSecondOpinion() {
  const H = RF.prospect.holdings;
  const L = Object.keys(H);
  const ov = RF.prospect.overlap;
  const rows = L.map((f) => {
    const g = RF.grades[f];
    const d = H[f];
    const maxOv = Math.max(...L.filter((b) => b !== f).map((b) => ov[f][b]));
    return { f, g, d, fee: (d * ER[f]) / 100, idxFee: (d * (1 - g.active_share) * (ER[f] - INDEX_FEE)) / 100, maxOv };
  });
  const tot = rows.reduce((s, r) => s + r.d, 0);
  const fees = rows.reduce((s, r) => s + r.fee, 0);
  const idxFees = rows.reduce((s, r) => s + r.idxFee, 0);
  const nIdx = rows.filter((r) => isIndexLike(r.g)).length;
  const cg = ["AGTHX", "AMCPX", "ANCFX"];
  const cgOv = [ov.AGTHX.AMCPX, ov.AGTHX.ANCFX, ov.AMCPX.ANCFX];
  const { eff } = effN(ov, L);
  const top = RF.prospect.look.top[0];
  return (
    <div className="space-y-4" data-testid="persona-ria-second">
      <PainBanner testid="persona-ria-second-pain" persona="RIA · PROSPECT SECOND OPINION"
        pain="A prospect brought another advisor's statement — how do I show in two minutes what's wrong, and why my approach is better?"
        answer="closet indexers, fees paid for index exposure, hidden overlap" />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Headline testid="ria-second-headline">{nIdx} of {L.length} funds are index-like · {Math.round((idxFees / fees) * 100)}% of the {usd(fees)} annual fee pays for index exposure · 3 Capital Group funds overlap {Math.floor(Math.min(...cgOv))}–{Math.floor(Math.max(...cgOv))}%</Headline>
          <div className="text-sm text-muted-foreground mt-1">Prospect: J. Carter household (sample) · {usd(tot)} in 6 mutual funds · real fund holdings & active share as of {OBS_F}</div>
        </div>
        <Button size="sm" onClick={() => toast("Mockup — proposal PDF not wired yet")} data-testid="ria-second-proposal-btn"><FileDown className="w-4 h-4 mr-1" />Generate proposal PDF</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testid="ria-second-kpi-fees" label="Fund fees / year" value={usd(fees)} sub={`${((fees / tot) * 100).toFixed(2)}% blended`} />
        <Kpi testid="ria-second-kpi-idxfees" label="…paid on index-like part" value={usd(idxFees)} tone="text-amber-400" sub={`${Math.round((idxFees / fees) * 100)}% of all fees`} />
        <Kpi testid="ria-second-kpi-eff" label="Effective managers" value={eff.toFixed(1)} tone="text-amber-400" sub={`of ${L.length} funds owned`} />
        <Kpi testid="ria-second-kpi-top10" label="Top-10 stocks" value={`${RF.prospect.look.top10_pct}%`} sub="of the whole portfolio" />
        <Kpi testid="ria-second-kpi-top" label="Biggest hidden stock" value={top.symbol} sub={`${top.pct.toFixed(1)}% via ${top.n_funds} funds`} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 items-start">
        <Card className="xl:col-span-3"><CardContent className="pt-4 overflow-x-auto">
          <SectionTitle>Fund-by-fund</SectionTitle>
          <table className="w-full text-xs" data-testid="ria-second-table">
            <thead className="border-b border-border text-muted-foreground"><tr>
              <th className="text-left py-1.5 px-2">Fund</th><th className="text-right px-2">Value</th><th className="text-right px-2">Active share</th>
              <th className="text-right px-2">Fee</th><th className="text-right px-2">Active-part fee</th><th className="text-right px-2">Max overlap</th><th className="text-right px-2">Verdict</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.f} className="border-b border-border/40" data-testid={`ria-second-row-${r.f}`}>
                  <td className="py-2 px-2"><span className="font-mono font-bold">{r.f}</span> <span className="text-[10px] text-muted-foreground">{SHORT[r.f]}</span></td>
                  <td className="text-right px-2 font-mono">{usd(r.d)}</td>
                  <td className={`text-right px-2 font-mono ${isIndexLike(r.g) ? "text-amber-400" : ""}`}>{(r.g.active_share * 100).toFixed(1)}%</td>
                  <td className="text-right px-2 font-mono">{ER[r.f].toFixed(2)}%</td>
                  <td className="text-right px-2 font-mono font-bold">{aer(r.g).toFixed(2)}%</td>
                  <td className={`text-right px-2 font-mono ${r.maxOv >= 50 ? "text-red-400" : ""}`}>{r.maxOv.toFixed(0)}%</td>
                  <td className="text-right px-2">{isIndexLike(r.g) ? <Pill tone="alert">INDEX-LIKE</Pill> : <Pill tone={["A", "B"].includes(r.g.letter) ? "good" : "bad"}>ACTIVE · GRADE {r.g.letter}</Pill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
        <Card className="xl:col-span-2"><CardContent className="pt-4 overflow-x-auto">
          <SectionTitle>Overlap between the funds</SectionTitle>
          <table className="text-[11px] font-mono" data-testid="ria-second-matrix">
            <thead><tr><th />{L.map((f) => <th key={f} className="px-0.5 py-1 text-muted-foreground font-normal text-[10px]">{f}</th>)}</tr></thead>
            <tbody>
              {L.map((a) => (
                <tr key={a}>
                  <td className="pr-2 text-muted-foreground text-[10px]">{a}</td>
                  {L.map((b) => (
                    <td key={b} className="p-0.5"><div className={`w-10 h-8 rounded flex items-center justify-center ${a === b ? "bg-zinc-900 text-zinc-600" : heat(ov[a][b])}`}>{a === b ? "—" : ov[a][b].toFixed(0)}</div></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-muted-foreground mt-2">{cg.join(", ")} are all Capital Group funds — three fees for largely the same portfolio.</div>
        </CardContent></Card>
      </div>
      <Card data-testid="ria-second-proposal"><CardContent className="pt-4">
        <SectionTitle right={<Pill tone="good">YOUR PROPOSAL (SAMPLE)</Pill>}>What we would do differently</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[13px]">
          <div className="rounded border border-border/70 p-3"><b>1 · Stop paying active fees for the index.</b> Hold the index-like core in a low-cost index fund: ≈ <b>{usd(idxFees)}/yr</b> saved on today's mix — about <b>{usd(idxFees * ((1.06 ** 10 - 1) / 0.06))}</b> over 10 years at 6%.</div>
          <div className="rounded border border-border/70 p-3"><b>2 · Fewer, truly different managers.</b> Replace three overlapping Capital Group funds with one high-conviction active sleeve.</div>
          <div className="rounded border border-border/70 p-3"><b>3 · Pay only for real active positioning.</b> Use funds with ≥80% active share and a top positioning grade (e.g. MFEGX, PRGFX — grade A).</div>
        </div>
        <div className="text-[10px] text-muted-foreground mt-3">Active-part fee = (fee − (1 − active share) × 0.03%) ÷ active share. Active share, holdings overlap and grades: real platform N-PORT data ({OBS_F}); fees approximate; household and amounts are sample. Not investment advice.</div>
      </CardContent></Card>
    </div>
  );
}
